import { DeleteOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
    Drawer,
    Flex,
    List
} from "antd";
import { FC } from "react";
import { toast } from "react-toastify";

import { deleteImportConfiguration, getImportConfigurations } from "../../api/template/template";
import { useUserData } from "../../hooks/useUserProfile";
import { setImportConfigurations, selectImportConfiguration} from "../../store/slices/dataverseSlice";
import { ImportConfiguration } from "../../types/importConfiguration";
import { createErrorMessage } from "../../utils/createErrorMessage";

interface ImportConfigurationsDrawerProps {
    isOpen: boolean;
    onClose: () => void;
}

export const ImportConfigurationsDrawer: FC<ImportConfigurationsDrawerProps> = ({ isOpen, onClose }) => {
    const { organizationId, tenantId } = useUserData();

    const { data, isLoading, isError, error, refetch, isFetched } = useQuery({
        queryKey: ['importConfigurations', 'dataverse'],
        queryFn: async () => {
            // Fetch import configurations from the API
            try {
                const response = await getImportConfigurations(
                    organizationId,
                    tenantId,
                    "DataverseAdapter"
                );

                if (!response) {
                    throw new Error('Network response was not ok');
                }

                setImportConfigurations(response.data.data);
                return response.data.data;
            } catch (error) {
                console.error('Error fetching import configurations:', error);
                throw error;
            }
        },
        enabled: !!organizationId && !!tenantId,
        gcTime: 0, // Disable caching to always fetch fresh data        
    });

    const handleConfigurationDelete = async (config: ImportConfiguration) => {
        try {
          await deleteImportConfiguration(organizationId, tenantId, config.id);
          toast.success("Configuration deleted successfully");
          refetch();
        } catch (error) {
          toast.error(createErrorMessage(error));
        }
    };

    const renderDrawerContent = () => {
        if (isLoading) return <p>Loading import configurations...</p>;

        if (isError) return <p>Error loading import configurations: {error.message}</p>;

        if (!data || data.length === 0) return <p>No import configurations found.</p>;

        return (
            <List
                dataSource={data}
                renderItem={(item: ImportConfiguration) => (
                    <List.Item
                        key={item.id}
                        onClick={() => {
                            selectImportConfiguration(item);
                        }}
                        style={{ cursor: 'pointer', marginBottom: '8px' }}
                    >
                        <Flex justify="space-between" align="center" style={{ width: '100%' }}>
                            {item.name}
                            <DeleteOutlined
                                style={{ color: 'red', cursor: 'pointer' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleConfigurationDelete(item);
                                }}
                            />
                        </Flex>
                    </List.Item>
                )}
            />
        );
    }

    return (
        <Drawer
            title="Import Configurations"
            placement="right"
            onClose={onClose}
            open={isOpen}
            width={600}
            destroyOnHidden
            afterOpenChange={(open) => {
                if (open && isFetched) {
                    // Reset the import configuration when the drawer opens
                    refetch();
                }
            }}
        >
            {renderDrawerContent()}
        </Drawer>
    );
};